import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Card, CardContent, CardHeader, CardTitle, CardDescription,
} from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
    Loader2, Search, AlertTriangle, Download, CheckCircle2, Square,
    ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';


/**
 * PartnerSettlementView — operational reconciliation surface.
 *
 * Reuses ``repro_jobs`` + ``supplier_purchases``. Settlement basis is
 * ``repro_jobs.status = "Completed"`` + ``delivered_to_factory_at``
 * inside range. Reconciliation is an accounting-only flag persisted
 * on the linked ``supplier_purchases`` doc — it does NOT touch the
 * artwork job status, customer approval, purchase amount, or any
 * production workflow.
 *
 * Sorting is client-side over the already-loaded result set — the
 * settlement query is capped at a few thousand rows and paginating
 * server-side would only complicate the UX.
 */


const _defaultFrom = () => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
};
const _defaultTo = () => new Date().toISOString().slice(0, 10);


const fmtMoney = (v, cur) => {
    if (v === null || v === undefined) return '—';
    try {
        return new Intl.NumberFormat('en-DK', {
            style: 'currency', currency: cur || 'EUR',
        }).format(v);
    } catch {
        return `${v.toFixed(2)} ${cur || ''}`.trim();
    }
};


const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-GB',
            { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return iso; }
};


// Column sort accessors — encapsulated so the click handler and the
// comparator agree on how to extract the sort key from a row. Keys
// that must be numeric are unwrapped from the row payload; strings
// are lowercased so "Aardvark" and "aardvark" sort identically.
const SORT_ACCESSORS = {
    order_number:              (r) => (r.order_number ?? -Infinity),
    supplier_purchase_po_number: (r) => (r.supplier_purchase_po_number || '').toLowerCase(),
    catalogue_number:          (r) => (r.catalogue_number || '').toLowerCase(),
    order_title:               (r) => (r.order_title || '').toLowerCase(),
    media_type_display:        (r) => (r.media_type_display || '').toLowerCase(),
    delivered_to_factory_at:   (r) => (r.delivered_to_factory_at || ''),
    partner_name:              (r) => (r.partner_name || '').toLowerCase(),
    // Sort by numeric amount only — currency drift is intentional so
    // "big NOK invoice" vs "small EUR invoice" doesn't lie.
    total_amount:              (r) => (r.total_amount ?? -Infinity),
    // Reconciled first when descending, unreconciled first when
    // ascending → treat as boolean → 1/0.
    reconciled:                (r) => (r.reconciled ? 1 : 0),
};


const SortIcon = ({ active, direction }) => {
    if (!active) return <ArrowUpDown className="h-3 w-3 inline ml-1 text-slate-400 shrink-0" />;
    return direction === 'asc'
        ? <ArrowUp className="h-3 w-3 inline ml-1 text-slate-700 shrink-0" />
        : <ArrowDown className="h-3 w-3 inline ml-1 text-slate-700 shrink-0" />;
};


const SortableHeader = ({ label, sortKey, currentKey, currentDir, onSort, align = 'left', testIdSuffix }) => (
    <TableHead className={align === 'right' ? 'text-right' : ''}>
        <button
            type="button"
            onClick={() => onSort(sortKey)}
            className={`inline-flex items-center gap-0 font-medium hover:text-slate-900 ${
                currentKey === sortKey ? 'text-slate-900' : 'text-slate-500'
            }`}
            data-testid={`settlement-sort-${testIdSuffix || sortKey}`}
        >
            {label}
            <SortIcon active={currentKey === sortKey} direction={currentDir} />
        </button>
    </TableHead>
);


export function PartnerSettlementView() {
    const [partners, setPartners] = useState([]);
    const [partnerId, setPartnerId] = useState('');
    const [fromDate, setFromDate] = useState(_defaultFrom());
    const [toDate, setToDate] = useState(_defaultTo());
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reconciling, setReconciling] = useState(false);
    const [selected, setSelected] = useState(new Set());
    // Sort state — column key + direction. Default: completion date,
    // ascending (matches the backend's natural order and the way an
    // admin reads a partner invoice top-to-bottom).
    const [sortKey, setSortKey] = useState('delivered_to_factory_at');
    const [sortDir, setSortDir] = useState('asc');

    // Load repro partners once — reuse existing /partners endpoint.
    useEffect(() => {
        const load = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${API_BASE}/api/partners?type=Repro`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setPartners(res.data || []);
            } catch {
                toast.error('Failed to load partners');
            }
        };
        load();
    }, []);

    const runSettlement = useCallback(async () => {
        if (!partnerId) {
            toast.error('Select a partner first');
            return;
        }
        setLoading(true);
        setSelected(new Set());
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${API_BASE}/api/artwork/settlement`,
                {
                    params: { partner_id: partnerId, from_date: fromDate, to_date: toDate },
                    headers: { Authorization: `Bearer ${token}` },
                },
            );
            setResult(res.data);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Settlement query failed');
        } finally {
            setLoading(false);
        }
    }, [partnerId, fromDate, toDate]);

    // Click a header → same column toggles asc ↔ desc; new column
    // starts ascending. Simple, predictable, matches Excel.
    const handleSort = (key) => {
        if (key === sortKey) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    // Materialize sorted rows only when data or sort state actually
    // moves — the underlying settlement fetch is cheap but sorting
    // 5,000 rows on every render is wasteful.
    const sortedRows = useMemo(() => {
        const rows = result?.rows || [];
        const accessor = SORT_ACCESSORS[sortKey];
        if (!accessor) return rows;
        const cp = [...rows].sort((a, b) => {
            const av = accessor(a);
            const bv = accessor(b);
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return cp;
    }, [result, sortKey, sortDir]);

    const selectableRows = useMemo(
        () => sortedRows.filter((r) => r.supplier_purchase_id),
        [sortedRows],
    );

    const toggleRow = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAllVisible = () => {
        const nonReconciled = selectableRows.filter((r) => !r.reconciled).map((r) => r.supplier_purchase_id);
        if (nonReconciled.every((id) => selected.has(id)) && nonReconciled.length > 0) {
            setSelected((prev) => {
                const next = new Set(prev);
                nonReconciled.forEach((id) => next.delete(id));
                return next;
            });
        } else {
            setSelected(new Set([...selected, ...nonReconciled]));
        }
    };

    const runReconcile = async (asReconciled) => {
        if (selected.size === 0) {
            toast.error('Select at least one row');
            return;
        }
        setReconciling(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE}/api/artwork/settlement/reconcile`,
                {
                    supplier_purchase_ids: Array.from(selected),
                    reconciled: asReconciled,
                },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            toast.success(
                `${selected.size} row${selected.size === 1 ? '' : 's'} marked as ${asReconciled ? 'Reconciled' : 'Not reconciled'}`,
            );
            await runSettlement();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Reconciliation failed');
        } finally {
            setReconciling(false);
        }
    };

    const exportCsv = () => {
        if (!sortedRows.length) return;
        const header = [
            'Order #', 'PO #', 'Catalogue', 'Artist / Title', 'Media type',
            'Completion date', 'Partner',
            'Expected partner cost', 'Currency',
            'Reconciled', 'Reconciled at', 'Reconciled by',
        ];
        const rows = sortedRows.map((r) => [
            r.order_number ?? '',
            r.supplier_purchase_po_number ?? '',
            r.catalogue_number ?? '',
            r.order_title ?? '',
            r.media_type_display ?? '',
            fmtDate(r.delivered_to_factory_at),
            r.partner_name ?? '',
            r.total_amount ?? '',
            r.currency ?? '',
            r.reconciled ? 'Yes' : 'No',
            r.reconciled_at ?? '',
            r.reconciled_by_name ?? '',
        ]);
        const csv = [header, ...rows]
            .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `settlement_${result.partner_name || result.partner_id}_${fromDate}_${toDate}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const nonReconciledSelectable = selectableRows.filter((r) => !r.reconciled).length;
    const allSelectedChecked = nonReconciledSelectable > 0 && selectableRows
        .filter((r) => !r.reconciled)
        .every((r) => selected.has(r.supplier_purchase_id));

    const sortProps = { currentKey: sortKey, currentDir: sortDir, onSort: handleSort };

    return (
        <div className="space-y-6" data-testid="partner-settlement-view">
            <Card>
                <CardHeader>
                    <CardTitle>Partner Settlement / Reconciliation</CardTitle>
                    <CardDescription>
                        Compare an incoming partner invoice against the artwork jobs Flow completed in a period. Uses
                        <code className="mx-1 text-xs bg-slate-100 px-1 rounded">delivered_to_factory_at</code>
                        as the settlement date so edits to old jobs never leak into a later period. Reconciliation is an
                        accounting flag — it does not change any job, approval or workflow status.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                            <Label htmlFor="settlement-partner">Artwork partner</Label>
                            <Select value={partnerId} onValueChange={setPartnerId}>
                                <SelectTrigger id="settlement-partner" data-testid="settlement-partner-select">
                                    <SelectValue placeholder="Pick partner…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {partners.map((p) => (
                                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="settlement-from">From</Label>
                            <Input
                                id="settlement-from"
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                data-testid="settlement-from"
                            />
                        </div>
                        <div>
                            <Label htmlFor="settlement-to">To</Label>
                            <Input
                                id="settlement-to"
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                data-testid="settlement-to"
                            />
                        </div>
                        <div className="flex items-end">
                            <Button
                                onClick={runSettlement}
                                disabled={loading || !partnerId}
                                data-testid="settlement-run"
                                className="w-full"
                            >
                                {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                                Run
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {result && (
                <Card data-testid="settlement-results">
                    <CardHeader className="flex flex-row items-start justify-between gap-3 flex-wrap">
                        <div>
                            <CardTitle>{result.partner_name}</CardTitle>
                            <CardDescription>
                                {fmtDate(result.from_date + 'T00:00:00Z')} → {fmtDate(result.to_date + 'T00:00:00Z')}
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            {selected.size > 0 && (
                                <>
                                    <span className="text-xs text-slate-500">
                                        {selected.size} selected
                                    </span>
                                    <Button
                                        size="sm"
                                        onClick={() => runReconcile(true)}
                                        disabled={reconciling}
                                        data-testid="settlement-mark-reconciled"
                                    >
                                        {reconciling ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                                        Mark as Reconciled
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => runReconcile(false)}
                                        disabled={reconciling}
                                        data-testid="settlement-unmark-reconciled"
                                    >
                                        <Square className="h-4 w-4 mr-1" />
                                        Unmark
                                    </Button>
                                </>
                            )}
                            {sortedRows.length > 0 && (
                                <Button variant="outline" size="sm" onClick={exportCsv} data-testid="settlement-export-csv">
                                    <Download className="h-4 w-4 mr-1" /> Export CSV
                                </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[40px]">
                                        <Checkbox
                                            checked={allSelectedChecked}
                                            onCheckedChange={selectAllVisible}
                                            disabled={nonReconciledSelectable === 0}
                                            aria-label="Select all non-reconciled rows"
                                            data-testid="settlement-select-all"
                                        />
                                    </TableHead>
                                    <SortableHeader label="Order #" sortKey="order_number" {...sortProps} />
                                    <SortableHeader label="PO #" sortKey="supplier_purchase_po_number" {...sortProps} testIdSuffix="po" />
                                    <SortableHeader label="Catalogue" sortKey="catalogue_number" {...sortProps} />
                                    <SortableHeader label="Artist / Title" sortKey="order_title" {...sortProps} />
                                    <SortableHeader label="Media type" sortKey="media_type_display" {...sortProps} testIdSuffix="media" />
                                    <SortableHeader label="Completion" sortKey="delivered_to_factory_at" {...sortProps} testIdSuffix="completion" />
                                    <SortableHeader label="Partner" sortKey="partner_name" {...sortProps} testIdSuffix="partner" />
                                    <SortableHeader label="Expected cost" sortKey="total_amount" {...sortProps} align="right" testIdSuffix="cost" />
                                    <SortableHeader label="Reconciled" sortKey="reconciled" {...sortProps} />
                                </TableRow>
                            </TableHeader>
                            <TableBody data-testid="settlement-rows">
                                {sortedRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} className="text-center py-6 text-slate-400" data-testid="settlement-empty">
                                            No completed artwork jobs for this partner in the selected period.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {sortedRows.map((r) => {
                                    const rowChecked = r.supplier_purchase_id && selected.has(r.supplier_purchase_id);
                                    return (
                                        <TableRow
                                            key={r.artwork_job_id}
                                            className={r.reconciled ? 'bg-emerald-50/40' : (r.has_cost ? '' : 'bg-amber-50/50')}
                                            data-testid={`settlement-row-${r.artwork_job_id}`}
                                        >
                                            <TableCell>
                                                <Checkbox
                                                    checked={!!rowChecked}
                                                    onCheckedChange={() => toggleRow(r.supplier_purchase_id)}
                                                    disabled={!r.supplier_purchase_id}
                                                    aria-label={`Select row for order ${r.order_number}`}
                                                    data-testid={`settlement-row-checkbox-${r.artwork_job_id}`}
                                                />
                                            </TableCell>
                                            <TableCell className="font-mono">{r.order_number ?? '—'}</TableCell>
                                            <TableCell className="font-mono">{r.supplier_purchase_po_number ?? '—'}</TableCell>
                                            <TableCell className="font-mono">{r.catalogue_number ?? '—'}</TableCell>
                                            <TableCell>{r.order_title ?? '—'}</TableCell>
                                            <TableCell>{r.media_type_display ?? '—'}</TableCell>
                                            <TableCell className="text-slate-600 whitespace-nowrap">
                                                {fmtDate(r.delivered_to_factory_at)}
                                            </TableCell>
                                            <TableCell>{r.partner_name ?? '—'}</TableCell>
                                            <TableCell className="text-right">
                                                {r.has_cost ? (
                                                    <span className="font-mono">
                                                        {fmtMoney(r.total_amount, r.currency)}
                                                    </span>
                                                ) : (
                                                    <Badge
                                                        className="bg-amber-100 text-amber-800 border-amber-300 border"
                                                        data-testid={`settlement-missing-cost-${r.artwork_job_id}`}
                                                    >
                                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                                        Missing cost
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {r.reconciled ? (
                                                    <Badge
                                                        className="bg-emerald-100 text-emerald-800 border-emerald-300 border"
                                                        title={r.reconciled_by_name ? `by ${r.reconciled_by_name}` : ''}
                                                        data-testid={`settlement-reconciled-${r.artwork_job_id}`}
                                                    >
                                                        <CheckCircle2 className="h-3 w-3 mr-1" />
                                                        Reconciled
                                                        {r.reconciled_at && (
                                                            <span className="ml-1 text-[10px] font-normal opacity-80">
                                                                {fmtDate(r.reconciled_at)}
                                                            </span>
                                                        )}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-xs text-slate-400" data-testid={`settlement-not-reconciled-${r.artwork_job_id}`}>
                                                        Not reconciled
                                                    </span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {/* Summary footer */}
                        <div
                            className="mt-4 border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4"
                            data-testid="settlement-summary"
                        >
                            <div className="text-sm space-y-1">
                                <div>
                                    <span className="text-slate-500">Total jobs in period:</span>{' '}
                                    <span className="font-medium" data-testid="settlement-total-count">{result.total_count}</span>
                                </div>
                                <div className="text-emerald-700 flex items-center gap-1" data-testid="settlement-reconciled-count">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Reconciled: <span className="font-medium">{result.reconciled_count}</span>
                                </div>
                                <div className="text-slate-600" data-testid="settlement-not-reconciled-count">
                                    Not reconciled: <span className="font-medium">{result.not_reconciled_count}</span>
                                </div>
                                {result.missing_cost_count > 0 && (
                                    <div className="text-amber-700 flex items-center gap-1" data-testid="settlement-missing-count">
                                        <AlertTriangle className="h-3 w-3" />
                                        Missing cost: <span className="font-medium">{result.missing_cost_count}</span>
                                        <span className="text-slate-500 ml-1">(excluded from totals)</span>
                                    </div>
                                )}
                            </div>
                            <div className="text-right space-y-1" data-testid="settlement-currency-totals">
                                {result.totals_by_currency.length === 0 && (
                                    <div className="text-slate-400 text-sm">No priced jobs in period.</div>
                                )}
                                {result.totals_by_currency.map((c) => (
                                    <div key={c.currency} className="text-sm">
                                        <div>
                                            <span className="text-slate-500 mr-2">
                                                Expected {c.currency} total ({c.job_count} job{c.job_count === 1 ? '' : 's'}):
                                            </span>
                                            <span className="font-semibold font-mono">
                                                {fmtMoney(c.total_amount, c.currency)}
                                            </span>
                                        </div>
                                        {c.reconciled_job_count > 0 && (
                                            <div className="text-xs text-emerald-700">
                                                <span className="mr-2">Reconciled ({c.reconciled_job_count} of {c.job_count}):</span>
                                                <span className="font-medium font-mono">
                                                    {fmtMoney(c.reconciled_amount, c.currency)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

export default PartnerSettlementView;
